/**
 * Configurações › Automação e IA › IA: preços por modelo, cotação, limites do Assistente e medição de uso (por mês, função, modelo,
 * pessoa e projeto). Serve para decidir com números: manter a IA paga, ajustar limites, desligar ou IA própria.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card, ErrorBox, Loading, useAct } from "./kit";

interface Spend { brl: number; calls: number; tokens: number; unpriced: boolean }
interface Usage {
  model: string; models: string[];
  prices: Record<string, { inUsd: number; outUsd: number }>; usdBrl: number | null; userLimitBrl: number | null; userLimits: Record<string, number | null>;
  items: Array<{ month: string; feature: string; model: string; calls: number; failed: number; inputTokens: number; outputTokens: number; cacheWriteTokens: number; cacheReadTokens: number; avgMs: number; costUsd: number | null }>;
  communications: Array<{ month: string; count: number; projectedUsd: number | null }>;
  avgPerCommunication: { inputTokens: number; outputTokens: number; costUsd: number | null } | null;
  month: { users: Array<Spend & { userId: string; name: string; limitBrl: number | null }>; projects: Array<Spend & { projectId: string; name: string; limitBrl: number | null }> };
  people: Array<{ id: string; displayName: string }>;
}

const FEATURE: Record<string, string> = { resumo_intimacao: "Resumo de intimação", resumo_processo: "Resumo do processo", rascunho_portal: "Rascunho para o cliente", assistente: "Assistente" };
const n = (v: number) => v.toLocaleString("pt-BR");
const month = (m: string) => { const [y, mm] = m.split("-"); return `${mm}/${y}`; };
const num = (s: string): number | null => (s.trim() === "" ? null : Number(s.replace(",", ".")));
const reais = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `R$ ${v.toFixed(2).replace(".", ",")}`);

export function AiUsagePanel(): JSX.Element {
  const { data, error } = useQuery({ queryKey: ["config-ia-uso"], queryFn: () => api.get<Usage>("/config/ia/uso?meses=6") });
  const [prices, setPrices] = useState<Record<string, { in: string; out: string }>>({});
  const [rate, setRate] = useState("");
  const [limit, setLimit] = useState("");
  const [exc, setExc] = useState<Array<{ userId: string; value: string }>>([]);
  useEffect(() => {
    if (!data) return;
    setPrices(Object.fromEntries(data.models.map((m) => [m, { in: data.prices[m]?.inUsd?.toString() ?? "", out: data.prices[m]?.outUsd?.toString() ?? "" }])));
    setRate(data.usdBrl?.toString() ?? "");
    setLimit(data.userLimitBrl?.toString() ?? "");
    setExc(Object.entries(data.userLimits).map(([userId, v]) => ({ userId, value: v === null ? "" : String(v) })));
  }, [data]);

  const save = useAct(() => {
    const p: Record<string, { inUsd: number; outUsd: number }> = { ...(data?.prices ?? {}) };
    for (const [m, v] of Object.entries(prices)) {
      const i = num(v.in); const o = num(v.out);
      if (i !== null && o !== null) p[m] = { inUsd: i, outUsd: o }; else delete p[m];
    }
    const userLimits = Object.fromEntries(exc.filter((x) => x.userId).map((x) => [x.userId, num(x.value)]));
    return api.put("/config/ia", { prices: p, usdBrl: num(rate), userLimitBrl: num(limit), userLimits });
  }, [["config-ia-uso"], ["config-ia"], ["assistente-estado"]], undefined, "Configuração da IA salva");

  const usd = (v: number | null) => {
    if (v === null) return "—";
    return `US$ ${v.toFixed(2)}${data?.usdBrl ? ` ≈ ${reais(v * data.usdBrl)}` : ""}`;
  };
  const months = data ? [...new Set([...data.items.map((x) => x.month), ...data.communications.map((x) => x.month)])].sort().reverse() : [];

  if (!data) return <Card title="Custos e limites da IA">{error ? <ErrorBox error={error} /> : <Loading />}</Card>;
  return (
    <>
      <Card title="Custos e limites da IA">
        <div className="stack gap-12">
          <div className="muted small">
            Copie os preços da tabela do provedor (US$ por milhão de tokens) para cada modelo liberado no servidor. O cache (instruções e
            arquivos de projeto repetidos) é calculado automaticamente: gravar custa 1,25× a entrada e ler custa 0,1×. Os valores são estimativa;
            a cobrança oficial é a da fatura do provedor.
          </div>
          <table className="table">
            <thead><tr><th>Modelo</th><th>US$ / milhão — entrada</th><th>US$ / milhão — saída</th></tr></thead>
            <tbody>{data.models.map((m) => (
              <tr key={m}>
                <td className="small">{m}{m === data.model && <span className="muted"> (padrão dos resumos)</span>}</td>
                <td><input className="input" inputMode="decimal" value={prices[m]?.in ?? ""} onChange={(e) => setPrices({ ...prices, [m]: { in: e.target.value, out: prices[m]?.out ?? "" } })} /></td>
                <td><input className="input" inputMode="decimal" value={prices[m]?.out ?? ""} onChange={(e) => setPrices({ ...prices, [m]: { in: prices[m]?.in ?? "", out: e.target.value } })} /></td>
              </tr>
            ))}</tbody>
          </table>
          <div className="row gap-8" style={{ flexWrap: "wrap" }}>
            <label className="stack gap-4"><span className="small">Cotação do dólar (R$)</span><input className="input" inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} /></label>
            <label className="stack gap-4"><span className="small">Assistente: limite por pessoa no mês (R$, vazio = sem limite)</span><input className="input" inputMode="decimal" value={limit} onChange={(e) => setLimit(e.target.value)} /></label>
          </div>
          <div className="stack gap-4">
            <span className="small"><strong>Exceções por pessoa</strong> <span className="muted">(valor vazio = sem limite para essa pessoa)</span></span>
            {exc.map((x, i) => (
              <div key={i} className="row gap-8 nowrap">
                <select className="input" value={x.userId} onChange={(e) => setExc(exc.map((y, j) => (j === i ? { ...y, userId: e.target.value } : y)))}>
                  <option value="">Escolha a pessoa…</option>
                  {data.people.map((p) => <option key={p.id} value={p.id}>{p.displayName}</option>)}
                </select>
                <input className="input" style={{ maxWidth: 140 }} inputMode="decimal" placeholder="R$" value={x.value} onChange={(e) => setExc(exc.map((y, j) => (j === i ? { ...y, value: e.target.value } : y)))} />
                <button className="link-btn" onClick={() => setExc(exc.filter((_, j) => j !== i))}>remover</button>
              </div>
            ))}
            <button className="btn small" style={{ alignSelf: "flex-start" }} onClick={() => setExc([...exc, { userId: "", value: "" }])}>Adicionar exceção</button>
          </div>
          <div><button className="btn primary" disabled={save.isPending} onClick={() => save.mutate()}>Salvar</button></div>
          <ErrorBox error={save.error} />
        </div>
      </Card>

      <Card title="Assistente — gasto do mês">
        <div className="stack gap-12">
          {data.month.users.length === 0 ? <div className="muted small">Ninguém usou o Assistente neste mês.</div> : (
            <table className="table">
              <thead><tr><th>Pessoa</th><th className="right">Perguntas</th><th className="right">Tokens</th><th className="right">Gasto</th><th className="right">Limite</th></tr></thead>
              <tbody>{data.month.users.map((u) => (
                <tr key={u.userId}>
                  <td>{u.name}</td><td className="right">{n(u.calls)}</td><td className="right">{n(u.tokens)}</td>
                  <td className="right">{reais(u.brl)}{u.unpriced && <span className="text-bad small" title="Há uso de modelo sem preço informado"> *</span>}</td>
                  <td className="right">{u.limitBrl === null ? "sem limite" : `${reais(u.limitBrl)} (${Math.round((u.brl / (u.limitBrl || 1)) * 100)}%)`}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
          {data.month.projects.length > 0 && (
            <table className="table">
              <thead><tr><th>Projeto</th><th className="right">Perguntas</th><th className="right">Gasto</th><th className="right">Limite</th></tr></thead>
              <tbody>{data.month.projects.map((p) => (
                <tr key={p.projectId}><td>{p.name}</td><td className="right">{n(p.calls)}</td><td className="right">{reais(p.brl)}</td><td className="right">{p.limitBrl === null ? "—" : reais(p.limitBrl)}</td></tr>
              ))}</tbody>
            </table>
          )}
        </div>
      </Card>

      <Card title="Uso da IA por mês">
        <div className="stack gap-12">
          {data.avgPerCommunication && (
            <div className="banner-ok">
              Cada resumo de intimação usa, em média, {n(data.avgPerCommunication.inputTokens)} tokens de entrada e {n(data.avgPerCommunication.outputTokens)} de saída
              {data.avgPerCommunication.costUsd !== null && <> — {usd(data.avgPerCommunication.costUsd)} por resumo</>}.
            </div>
          )}
          {months.length === 0 ? <div className="muted">Ainda não há uso registrado.</div> : (
            <table className="table">
              <thead><tr><th>Mês</th><th>Função</th><th>Modelo</th><th className="right">Pedidos</th><th className="right">Falhas</th><th className="right">Entrada</th><th className="right">Cache (lido)</th><th className="right">Saída</th><th className="right">Tempo médio</th><th className="right">Custo</th></tr></thead>
              <tbody>
                {months.flatMap((m) => {
                  const rows = data.items.filter((x) => x.month === m);
                  const c = data.communications.find((x) => x.month === m);
                  const total = rows.reduce((a, x) => a + (x.costUsd ?? 0), 0);
                  const missing = rows.some((x) => x.costUsd === null);
                  return [
                    ...rows.map((x) => (
                      <tr key={`${m}-${x.feature}-${x.model}`}>
                        <td>{month(m)}</td><td>{FEATURE[x.feature] ?? x.feature}</td><td className="small muted">{x.model}</td>
                        <td className="right">{n(x.calls)}</td><td className="right">{x.failed || ""}</td>
                        <td className="right">{n(x.inputTokens + x.cacheWriteTokens)}</td><td className="right">{n(x.cacheReadTokens)}</td><td className="right">{n(x.outputTokens)}</td>
                        <td className="right">{(x.avgMs / 1000).toFixed(1)} s</td><td className="right">{usd(x.costUsd)}</td>
                      </tr>
                    )),
                    <tr key={`${m}-total`} className="muted">
                      <td>{month(m)}</td>
                      <td colSpan={8}>
                        <strong>Total do mês: {rows.length ? `${usd(total)}${missing ? " (há modelo sem preço)" : ""}` : "sem uso"}</strong>
                        {c && <> · {n(c.count)} intimações recebidas{c.projectedUsd !== null && <> — resumir todas custaria ≈ {usd(c.projectedUsd)}</>}</>}
                      </td>
                      <td />
                    </tr>,
                  ];
                })}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </>
  );
}
